import React from 'react';
import { TooltipProvider } from '@onward-delivery/design';

import BucketTabs from './BucketTabs';
import OnboardingFilters from './OnboardingFilters';
import OnboardingRow from './OnboardingRow';
import PanelMessage from './PanelMessage';
import SelectionPanel from './SelectionPanel';
import { SHIPPER_BADGES, SHIPPER_BUCKETS } from '../constants';
import { useShipperOnboarding } from '../hooks';
import { describeShipperRow, shipperSelectionActions } from '../utils';

/**
 * The Shippers onboarding view: Account Setup, Integrations and Live, the active tab's cards, and the options panel
 * for the No Integrations Required toggle.
 *
 * @param {{ search: string }} props
 */
const ShipperOnboarding = ({ search }) => {
    const {
        loading,
        error,
        hasOrganization,
        bucket,
        setBucketKey,
        filters,
        setFilters,
        counts,
        bucketRows,
        selectedIds,
        selectedRows,
        toggleRow,
        clearSelection,
        busy,
        setIntegrationsRequired,
    } = useShipperOnboarding({ search });

    const { markNoIntegrations, requireIntegrations } = shipperSelectionActions(selectedRows, bucket.key);
    const panelButtons = [
        {
            key: 'NO_INTEGRATIONS',
            label: 'Mark No Integrations Required',
            variant: 'outline',
            show: markNoIntegrations.length > 0,
            disabled: busy,
            onClick: () => setIntegrationsRequired(markNoIntegrations, false),
        },
        {
            // Only flagged shippers get this, and they're never on the Integrations tab with the button above.
            key: 'REQUIRE_INTEGRATIONS',
            label: 'Require Integrations',
            variant: 'outline',
            show: requireIntegrations.length > 0,
            sizes: false,
            disabled: busy,
            onClick: () => setIntegrationsRequired(requireIntegrations, true),
        },
    ];

    let body;
    if (!hasOrganization) body = <PanelMessage>Your account is not associated with an organization.</PanelMessage>;
    else if (error) body = <PanelMessage>Couldn&apos;t load onboarding. Try refreshing the page.</PanelMessage>;
    else if (loading) body = <PanelMessage>Loading…</PanelMessage>;
    else if (!bucketRows.length) {
        body = (
            <PanelMessage>
                {filters.badges.length ? 'No shippers here match the filters.' : 'No shippers here.'}
            </PanelMessage>
        );
    } else {
        body = (
            <ul className="flex flex-col gap-2.5">
                {bucketRows.map((row) => (
                    <OnboardingRow
                        key={row.shipper_id}
                        row={row}
                        describe={describeShipperRow}
                        checked={selectedIds.has(row.shipper_id)}
                        onToggle={toggleRow}
                    />
                ))}
            </ul>
        );
    }

    // The design Tooltip throws without a provider above it; the carrier view brings its own the same way.
    return (
        <TooltipProvider delayDuration={200}>
            <div className="flex min-h-0 w-full flex-1 flex-col">
                <BucketTabs buckets={SHIPPER_BUCKETS} activeKey={bucket.key} counts={counts} onSelect={setBucketKey} />
                <section
                    className={`flex min-h-0 flex-1 flex-col rounded-b-lg rounded-tr-lg border ${bucket.panelClass}`}
                >
                    <div className="min-h-0 flex-1 overflow-y-auto p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <p className="text-[13px] text-slate-600">{bucket.subtitle}</p>
                            <OnboardingFilters
                                filters={filters}
                                onChange={setFilters}
                                badges={SHIPPER_BADGES}
                                typeOptions={[]}
                            />
                        </div>
                        {body}
                        {panelButtons.some((button) => button.show) ? (
                            <SelectionPanel
                                count={selectedRows.length}
                                buttons={panelButtons}
                                onClear={clearSelection}
                            />
                        ) : null}
                    </div>
                </section>
            </div>
        </TooltipProvider>
    );
};

export default ShipperOnboarding;
